import React, { useMemo } from "react";
import { Link } from "gatsby";
import { makeStyles } from "@material-ui/core/styles";
import { Button, Typography, Table, TableBody, TableCell, TableHead, TableRow } from "@material-ui/core";
import Helmet from "react-helmet";
import distance from "@turf/distance";
import Colors from "../constants/Colors";
import logoImage from "../assets/img/idea-option_color-logo.png";

const MAX_DISTANCE_MILES = 50;

const useStyles = makeStyles(() => ({
  root: {
    maxWidth: 960,
    margin: "0 auto",
    padding: "32px 24px 64px",
    fontFamily: "Lato, Verdana, Arial, sans-serif",
    color: Colors.mineShaft,
    backgroundColor: Colors.white,
    WebkitPrintColorAdjust: "exact",
    printColorAdjust: "exact",
    "@media print": {
      maxWidth: "100%",
      padding: "12px 8px 16px",
      margin: 0,
    },
  },
  printActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 16,
    marginBottom: 24,
    "@media print": {
      display: "none",
    },
  },
  logoContainer: {
    display: "flex",
    justifyContent: "flex-start",
    marginBottom: 24,
    "@media print": {
      marginBottom: 8,
    },
  },
  logo: {
    maxHeight: 70,
    width: "auto",
    "@media print": {
      maxHeight: 70,
    },
  },
  header: {
    marginBottom: 24,
    "@media print": {
      marginBottom: 10,
    },
  },
  pageLabel: {
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    fontSize: 12,
    fontWeight: 700,
    color: Colors.azure,
    marginBottom: 8,
    "@media print": {
      fontSize: 9,
      marginBottom: 4,
    },
  },
  pageTitle: {
    fontWeight: 700,
    marginBottom: 16,
    color: Colors.mineShaft,
    "@media print": {
      marginBottom: 8,
    },
  },
  clinicMeta: {
    marginTop: 16,
    padding: "16px 20px",
    borderRadius: 8,
    backgroundColor: Colors.catskillWhite,
    lineHeight: 1.5,
    fontSize: 14,
    "@media print": {
      marginTop: 8,
      padding: "8px 10px",
      fontSize: 10,
      lineHeight: 1.3,
    },
  },
  clinicTitle: {
    fontWeight: 700,
    color: Colors.mineShaft,
    marginBottom: 4,
    "@media print": {
      fontSize: 11,
      marginBottom: 2,
    },
  },
  clinicInfo: {
    fontSize: 13,
    color: Colors.tundora,
    marginTop: 4,
    "@media print": {
      fontSize: 9,
      marginTop: 2,
    },
  },
  resourcesSection: {
    marginTop: 20,
    "@media print": {
      marginTop: 8,
    },
  },
  resourcesHeader: {
    marginBottom: 12,
    fontWeight: 700,
    "@media print": {
      display: "none",
    },
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    "@media print": {
      fontSize: 9,
      width: "100%",
    },
  },
  tableHead: {
    backgroundColor: Colors.pickledBluewood,
    "@media print": {
      backgroundColor: Colors.pickledBluewood,
      WebkitPrintColorAdjust: "exact",
      printColorAdjust: "exact",
    },
  },
  tableHeaderCell: {
    color: Colors.white,
    fontWeight: 700,
    padding: "10px 8px",
    fontSize: 12,
    border: `1px solid ${Colors.kashmirBlue}`,
    "@media print": {
      padding: "4px 4px",
      fontSize: 8,
      lineHeight: 1.2,
    },
  },
  tableRow: {
    "&:nth-of-type(even)": {
      backgroundColor: Colors.catskillWhite,
      "@media print": {
        backgroundColor: Colors.catskillWhite,
        WebkitPrintColorAdjust: "exact",
        printColorAdjust: "exact",
      },
    },
    "@media print": {
      pageBreakInside: "avoid",
    },
  },
  tableCell: {
    padding: "10px 8px",
    border: `1px solid ${Colors.horizontalUnderlineGray}`,
    fontSize: 13,
    verticalAlign: "top",
    "@media print": {
      padding: "4px 4px",
      fontSize: 9,
      lineHeight: 1.2,
    },
  },
  resourceName: {
    fontWeight: 700,
    color: Colors.mineShaft,
    fontSize: 14,
    textAlign: "center",
    lineHeight: 1.2,
    "@media print": {
      fontSize: 10,
    },
  },
  resourceNameCell: {
    textAlign: "center",
    verticalAlign: "middle",
  },
  resourceCategory: {
    fontSize: 12,
    color: Colors.azure,
    fontWeight: 600,
    "@media print": {
      fontSize: 8,
    },
  },
  resourceCategoryCell: {
    textAlign: "center",
    verticalAlign: "middle",
  },
  resourceDistance: {
    fontSize: 12,
    color: Colors.tundora,
    whiteSpace: "nowrap",
    "@media print": {
      fontSize: 8,
    },
  },
  resourceAddress: {
    fontSize: 13,
    lineHeight: 1.4,
    color: Colors.mineShaft,
    "@media print": {
      fontSize: 9,
      lineHeight: 1.2,
    },
  },
  resourceContact: {
    fontSize: 12,
    lineHeight: 1.4,
    color: Colors.tundora,
    "@media print": {
      fontSize: 8,
      lineHeight: 1.2,
    },
  },
  resourceNotes: {
    fontSize: 12,
    color: Colors.tundora,
    fontStyle: "italic",
    marginTop: 4,
    "@media print": {
      fontSize: 9,
      marginTop: 2,
      lineHeight: 1.2,
    },
  },
  columnResourceName: {
    width: "18%",
    "@media print": {
      width: "18%",
    },
  },
  columnCategory: {
    width: "12%",
    "@media print": {
      width: "12%",
    },
  },
  columnDistance: {
    width: "8%",
    textAlign: "center",
    "@media print": {
      width: "8%",
    },
  },
  columnAddress: {
    width: "15%",
    "@media print": {
      width: "15%",
    },
  },
  columnContact: {
    width: "15%",
    "@media print": {
      width: "15%",
    },
  },
  columnNotes: {
    width: "17%",
    "@media print": {
      width: "17%",
    },
  },
  link: {
    color: Colors.azure,
    textDecoration: "none",
    wordBreak: "break-word",
    "@media print": {
      color: Colors.mineShaft,
      textDecoration: "underline",
      fontSize: 8,
    },
  },
  secondaryLink: {
    color: Colors.azure,
    fontWeight: 600,
    textDecoration: "none",
    marginRight: "auto",
  },
  emptyState: {
    fontStyle: "italic",
    color: Colors.tundora,
    padding: 20,
    textAlign: "center",
    "@media print": {
      padding: 10,
      fontSize: 9,
    },
  },
  clinicAddress: {
    marginTop: 4,
  },
  supportCopy: {
    marginTop: 24,
    color: Colors.tundora,
    fontSize: 12,
    fontStyle: "italic",
    textAlign: "center",
    "@media print": {
      marginTop: 8,
      fontSize: 8,
    },
  },
}));

const formatDistance = (value) => {
  if (value === null || typeof value === "undefined" || Number.isNaN(value)) {
    return null;
  }
  const rounded = Math.round(value);
  return `${rounded} mi`;
};

const hasLocation = (entity) =>
  entity &&
  entity.location &&
  typeof entity.location.longitude === "number" &&
  typeof entity.location.latitude === "number";

export default function ClinicCommunityResourcesPrintView({
  clinic,
  communityResources,
}) {
  const classes = useStyles();

  const clinicPoint = useMemo(() => {
    if (!hasLocation(clinic)) {
      return null;
    }
    return {
      type: "Point",
      coordinates: [
        clinic.location.longitude,
        clinic.location.latitude,
      ],
    };
  }, [clinic]);

  const resourcesWithinRange = useMemo(() => {
    if (!clinicPoint || !Array.isArray(communityResources)) {
      return [];
    }

    return communityResources
      .map((resource) => {
        if (!hasLocation(resource)) {
          return null;
        }

        try {
          const resourcePoint = {
            type: "Point",
            coordinates: [
              resource.location.longitude,
              resource.location.latitude,
            ],
          };
          const miles = distance(clinicPoint, resourcePoint, {
            units: "miles",
          });
          return {
            ...resource,
            distance: miles,
          };
        } catch (error) {
          console.warn(
            "Unable to calculate distance for resource:",
            resource.title,
            error
          );
          return null;
        }
      })
      .filter(
        (resource) =>
          resource &&
          typeof resource.distance === "number" &&
          resource.distance <= MAX_DISTANCE_MILES
      )
      .sort((a, b) => a.distance - b.distance);
  }, [clinicPoint, communityResources]);

  const handlePrint = () => {
    if (typeof window !== "undefined") {
      window.print();
    }
  };

  // Auto-print if ?autoprint=true is in the URL
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('autoprint') === 'true') {
        // Small delay to ensure page is fully rendered
        setTimeout(() => {
          window.print();
        }, 500);
      }
    }
  }, []);

  const clinicAddressMarkup =
    clinic && clinic.address
      ? { __html: clinic.address }
      : null;

  const backToClinicPath =
    clinic && clinic.slug ? `/suboxone-clinics/${clinic.slug}` : null;

  const printableTitle = clinic
    ? `Community resources near Ideal Option ${clinic.title}`
    : "Community resources";

  return (
    <div className={classes.root}>
      <Helmet>
        <title>{printableTitle}</title>
      </Helmet>
      <div className={classes.printActions}>
        {backToClinicPath && (
          <Link to={backToClinicPath} className={classes.secondaryLink}>
            Back to clinic page
          </Link>
        )}
        <Button variant="contained" color="primary" onClick={handlePrint}>
          Print this page
        </Button>
      </div>
      <div className={classes.logoContainer}>
        <img src={logoImage} alt="Ideal Option" className={classes.logo} />
      </div>
      <header className={classes.header}>
        <Typography variant="overline" className={classes.pageLabel}>
          Community Support
        </Typography>
        <Typography variant="h2" className={classes.pageTitle}>
          Community Resources Within 50 Miles
        </Typography>
        {clinic && (
          <div className={classes.clinicMeta}>
            <Typography variant="h3" className={classes.clinicTitle}>
              Ideal Option {clinic.title}
            </Typography>
            {clinicAddressMarkup && (
              <Typography
                component="div"
                variant="body2"
                className={classes.clinicInfo}
                dangerouslySetInnerHTML={clinicAddressMarkup}
              />
            )}
            {clinic.phone && (
              <Typography variant="body2" className={classes.clinicInfo}>
                Phone: {clinic.phone}
              </Typography>
            )}
          </div>
        )}
      </header>
      <section className={classes.resourcesSection}>
        {resourcesWithinRange.length === 0 ? (
          <Typography variant="body1" className={classes.emptyState}>
            No resources were found within 50 miles of this clinic. Please visit
            the community resources directory for more options.
          </Typography>
        ) : (
          <Table className={classes.table}>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell className={`${classes.tableHeaderCell} ${classes.resourceNameCell} ${classes.columnResourceName}`}>Resource Name</TableCell>
                <TableCell className={`${classes.tableHeaderCell} ${classes.resourceCategoryCell} ${classes.columnCategory}`}>Category</TableCell>
                <TableCell className={`${classes.tableHeaderCell} ${classes.columnDistance}`}>Distance</TableCell>
                <TableCell className={`${classes.tableHeaderCell} ${classes.columnAddress}`}>Address</TableCell>
                <TableCell className={`${classes.tableHeaderCell} ${classes.columnContact}`}>Contact</TableCell>
                <TableCell className={`${classes.tableHeaderCell} ${classes.columnNotes}`}>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {resourcesWithinRange.map((resource) => {
                const distanceLabel = formatDistance(resource.distance);
                const address = [resource.address, resource.city, resource.state, resource.zip]
                  .filter(Boolean)
                  .join(", ");
                const contactInfo = [];
                if (resource.phone) contactInfo.push(`Phone: ${resource.phone}`);
                if (resource.websiteUrl) {
                  contactInfo.push(
                    <span key="website">
                      Website:{" "}
                      <a
                        href={resource.websiteUrl}
                        className={classes.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {resource.websiteUrl}
                      </a>
                    </span>
                  );
                }
                return (
                  <TableRow key={resource.id} className={classes.tableRow}>
                    <TableCell className={`${classes.tableCell} ${classes.resourceNameCell} ${classes.columnResourceName}`}>
                      <div className={classes.resourceName}>{resource.title}</div>
                    </TableCell>
                    <TableCell className={`${classes.tableCell} ${classes.resourceCategoryCell} ${classes.columnCategory}`}>
                      {resource.category && (
                        <span className={classes.resourceCategory}>{resource.category}</span>
                      )}
                    </TableCell>
                    <TableCell className={`${classes.tableCell} ${classes.columnDistance}`}>
                      {distanceLabel && (
                        <span className={classes.resourceDistance}>{distanceLabel}</span>
                      )}
                    </TableCell>
                    <TableCell className={`${classes.tableCell} ${classes.columnAddress}`}>
                      <div className={classes.resourceAddress}>{address || "—"}</div>
                    </TableCell>
                    <TableCell className={`${classes.tableCell} ${classes.columnContact}`}>
                      <div className={classes.resourceContact}>
                        {contactInfo.length > 0 ? contactInfo.map((item, idx) => (
                          <div key={idx}>{item}</div>
                        )) : "—"}
                      </div>
                    </TableCell>
                    <TableCell className={`${classes.tableCell} ${classes.columnNotes}`}>
                      {resource.notes ? (
                        <div className={classes.resourceNotes}>{resource.notes}</div>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </section>
    </div>
  );
}

